import { produce } from 'immer';
import { PrimeurUserModel, ProductModel } from '@/models';
import { UntypedObject, valueof } from '@/types';
import { getApiUrl, API_URL } from '@/utils/api';

import ApiBase from './ApiBase';
import { WARN_BY_STATE, SPLIT_DESIGNATION_KEY } from '@/constants/primeur';

type ProductBaseModel = Pick<ProductModel, 'partnumber' | 'productName'>;

export default class PrimeurService extends ApiBase {
  static baseUrl = getApiUrl(API_URL.USER, '/primeurUser');

  saveAlertList(primeurUser: UntypedObject) {
    return this.api.putJSON(`${PrimeurService.baseUrl}/saveAlertList`, primeurUser);
  }

  getPrimeurUser({
    idUser, storeId, alerts, hashPrimeurUser = undefined, email = undefined, tel = undefined,
    warnByEmail = undefined, warnBySms = undefined, toCreate = false
  }) {
    return this.api.postJSON(`${PrimeurService.baseUrl}/getPrimeurUser`, {
      idUser,
      hashPrimeurUser,
      email,
      tel,
      storeId,
      warnByEmail,
      warnBySms,
      alerts,
      toCreate
    });
  }

  checkPrimeurUserMail(email: string) {
    return this.api.getJSON(`${PrimeurService.baseUrl}/checkEmailExisting?email=${encodeURIComponent(email)}`);
  }

  updatePrimeurUser({ idPrimeurUser, email, tel, countryPhone, warnByEmail, warnBySms }) {
    return this.api.putJSON(`${PrimeurService.baseUrl}/updatePrimeurUser`, {
      idPrimeurUser,
      email,
      tel,
      countryPhone,
      warnByEmail,
      warnBySms
    });
  }

  getHash(email: string) {
    return this.api.getJSON(`${PrimeurService.baseUrl}/getHash?email=${encodeURIComponent(email)}`);
  }

  getCurrentAlertIdList(designations?: { products?: { alert?: any, partnumber: string }[] }[]): string[] {
    const alertIdList: string[] = [];

    if (designations) {
      designations.forEach(({ products }) => {
        if (products) {
          products.forEach(({ alert, partnumber }) => {
            if (alert) {
              alertIdList.push(partnumber);
            }
          });
        }
      });
    }

    return alertIdList;
  }

  isWarnBy(primeurUser?: PrimeurUserModel, warnMode?: valueof<typeof WARN_BY_STATE>): boolean {
    let isSet = false;

    if (!primeurUser && warnMode === WARN_BY_STATE.EMAIL) {
      isSet = true;
    } else if (primeurUser) {
      if (warnMode === WARN_BY_STATE.EMAIL) {
        isSet = primeurUser.warnByEmail && !primeurUser.warnBySms;
      } else if (warnMode === WARN_BY_STATE.SMS) {
        isSet = !primeurUser.warnByEmail && primeurUser.warnBySms;
      } else if (warnMode === WARN_BY_STATE.EMAILANDSMS) {
        isSet = primeurUser.warnByEmail && primeurUser.warnBySms;
      }
    }

    return isSet;
  }

  formatProducts(products: ProductBaseModel[]): { partnumber: string, name: string, alert: false }[] {
    return products.map((product) => ({
      partnumber: product.partnumber,
      name: product.productName,
      alert: false
    }));
  }

  formatDesignations(previousDesignations: { [key: string]: ProductBaseModel[] }): { id: string, name: string; products: any[] }[] {
    return Object.entries(previousDesignations).map(([key, products]) => {
      const [name, id] = key.split(SPLIT_DESIGNATION_KEY);
      return {
        id,
        name,
        products: this.formatProducts(products)
      };
    });
  }

  updateDesignationAlerts(
    designations: { products: ProductBaseModel[] }[],
    alertCheck: (designation: any, product: ProductBaseModel
  ) => boolean) {
    return designations.map((designation) => this.updateOneDesignationAlerts(designation, alertCheck));
  }

  updateOneDesignationAlerts(designation, alertCheck) {
    return produce(designation, (dd) => {
      dd.products = dd.products.map((product) =>
        produce(product, (dp) => {
          dp.alert = alertCheck(designation, product);
        })
      );
    });
  }
}
